import React, { useState, useMemo } from "react";
import { CommandBarButton, Icon, IconButton, MessageBar, MessageBarType, SearchBox } from "@fluentui/react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

const StepsTable = ({
    steps,
    setSelectedStepId,
    loading,
    deleteOnClick,
    editOnClick,
    createOnClick,
    createDisabled,
    updateStepNumber,
    setup
}) => {
    let columns = [
       
        {
            key: "stepNumber",
            name: "Step",
            fieldName: "stepNumber",
            minWidth: 40,
            maxWidth: 40,
        },
        {
            key: "courseStepID",
            name: "Step ID",
            fieldName: "courseStepID",
            minWidth: 50,
            maxWidth: 70,
        },
        {
            key: "courseStepType",
            name: "Step Type",
            fieldName: "courseStepType",
            minWidth: 100,
            maxWidth: 200,
        },
        {
            key: "title",
            name: "Title",
            fieldName: "title",
            minWidth: 300,
            maxWidth: 500,
        },
        {
            key: "menu",
            name: "",
            minWidth: 30,
            maxWidth: 30,
            onRender: (item) => (
                <ContextualMenu
                    editOnClick={editOnClick}
                    deleteOnClick={deleteOnClick}
                    setSelectedStepId={setSelectedStepId}
                    id={item.crS_x_CRSStepID}
                    stepNumber={item.stepNumber}
                    updateStepNumber={updateStepNumber}
                />
            )
        },
        {
            key: "courseStepCategory",
            name: "Category",
            fieldName: "courseStepCategory",
            minWidth: 100,
            maxWidth: 120,
        },
        {
            key: "navSectionTitle",
            name: "Nav Section Title",
            fieldName: "navSectionTitle",
            minWidth: 180,
            maxWidth: 200,
        },
        {
            key: "onOffDefault",
            name: "Include",
            fieldName: "onOffDefault",
            minWidth: 100,
            maxWidth: 120,
            onRender: (item) => item.onOffDefault ? "Include" : "Exclude"
        },
        {
            key: "onOffRule",
            name: "Exception Rule",
            fieldName: "onOffRule",
            minWidth: 200,
            maxWidth: 300,
        }
    ];

    if(setup){
        columns = columns.filter(x => x.key !== "navSectionTitle") // hide Nav Section Title on setup steps
    }

    const items = steps ?? [];

    const [searchText, setSearchText] = useState("");

    const sortedItems = useMemo(() => {
        const sorted = [...items].sort((a, b) => a.stepNumber - b.stepNumber);
        if (!searchText) return sorted;
        const lower = searchText.toLowerCase();
        return sorted.filter(item =>
            (item.title && item.title.toLowerCase().includes(lower)) ||
            (item.courseStepType && item.courseStepType.toLowerCase().includes(lower)) ||
            (item.courseStepCategory && item.courseStepCategory.toLowerCase().includes(lower)) ||
            (item.courseStepID && String(item.courseStepID).includes(lower)) ||
            (item.stepNumber && String(item.stepNumber).includes(lower))
        );
    }, [items, searchText]);

    const onDragEnd = (result) => {
        if (!result.destination) return;

        const sourceIndex = result.source.index;
        const destIndex = result.destination.index;
        if (sourceIndex === destIndex) return;

        const draggedItem = sortedItems[sourceIndex];
        const targetItem = sortedItems[destIndex];

        // updateStepNumber moves a step to a specific step number; the server reorders the rest
        updateStepNumber(draggedItem.crS_x_CRSStepID, targetItem.stepNumber);
    };

    const headerStyle = {
        display: "flex",
        alignItems: "center",
        fontWeight: 600,
        fontSize: 12,
        color: "#605e5c",
        borderBottom: "1px solid #edebe9",
        padding: "8px 0",
    };

    const cellStyle = (col) => ({
        minWidth: col.minWidth,
        maxWidth: col.maxWidth,
        flex: col.maxWidth ? `0 0 ${col.maxWidth}px` : "1 1 0",
        padding: "0 8px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    });

    const isFiltered = !!searchText;

    const rowStyle = (isDragging) => ({
        display: "flex",
        alignItems: "center",
        padding: "6px 0",
        borderBottom: "1px solid #edebe9",
        background: isDragging ? "#deecf9" : "#fff",
        boxShadow: isDragging ? "0 2px 8px rgba(0,0,0,0.15)" : "none",
        cursor: isFiltered ? "default" : "grab",
    });

    return (
       <div style={{ position: "relative", width: "80vw" }}>
            <MessageBar
                messageBarType={MessageBarType.warning}
                isMultiline={false}
                dismissButtonAriaLabel="Close"
                styles={{ root: { marginBottom: 8 } }}
            >
                Steps have been reordered. Please double-check the step order.
            </MessageBar>

            <div style={{ marginBottom: 8, display: "flex", alignItems: "center" }}>
                <CommandBarButton
                    iconProps={{ iconName: "Add" }}
                    text="Create"
                    onClick={() => createOnClick()}
                    disabled={createDisabled}
                />
                <SearchBox
                    placeholder="Filter steps..."
                    onChange={(_, newValue) => setSearchText(newValue || "")}
                    styles={{ root: { marginLeft: "auto", width: 220 } }}
                    underlined
                />
            </div>

            {isFiltered && (
                <MessageBar
                    messageBarType={MessageBarType.info}
                    isMultiline={false}
                    styles={{ root: { marginBottom: 8 } }}
                >
                    Drag and drop is disabled while the filter is active. Clear the filter to reorder steps.
                </MessageBar>
            )}

            {/* Header row */}
            <div style={headerStyle}>
                <div style={{ minWidth: 30, maxWidth: 30, padding: "0 8px" }} />
                {columns.map((col) => (
                    <div key={col.key} style={cellStyle(col)}>
                        {col.name}
                    </div>
                ))}
            </div>

            {/* Drag and drop rows */}
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="steps-list">
                    {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps}>
                            {sortedItems.map((item, index) => (
                                <Draggable
                                    key={String(item.crS_x_CRSStepID)}
                                    draggableId={String(item.crS_x_CRSStepID)}
                                    index={index}
                                    isDragDisabled={isFiltered}
                                >
                                    {(provided, snapshot) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            style={{
                                                ...rowStyle(snapshot.isDragging),
                                                ...provided.draggableProps.style,
                                            }}
                                        >
                                            <div
                                                {...provided.dragHandleProps}
                                                style={{
                                                    minWidth: 30,
                                                    maxWidth: 30,
                                                    padding: "0 8px",
                                                    cursor: "grab",
                                                    display: "flex",
                                                    alignItems: "center",
                                                }}
                                                title="Drag to reorder"
                                            >
                                                <Icon iconName="GripperBarHorizontal" styles={{ root: { fontSize: 12, color: isFiltered ? "#c8c6c4" : "#605e5c" } }} />
                                            </div>
                                            {columns.map((col) => (
                                                <div key={col.key} style={cellStyle(col)}>
                                                    {col.onRender
                                                        ? col.onRender(item)
                                                        : item[col.fieldName]}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        </div>
    );
};

const moreIcon = { iconName: "More" };

const ContextualMenu = ({
    editOnClick,
    deleteOnClick,
    setSelectedStepId,
    id,
    stepNumber,
    updateStepNumber,
    ...rest
}) => {
    const menuProps = {
        items: [
            {
                key: "print",
                iconProps: { iconName: "Edit" },
                text: "Edit",
                onClick: () => {
                    setSelectedStepId(id);
                    editOnClick(id)},
            },
            {
                key: "Delete",
                iconProps: { iconName: "Delete" },
                text: "Delete",
                onClick: () =>  {
                    setSelectedStepId(id);
                    deleteOnClick(id)},
            },
            {
                key: "MoveUp",
                iconProps: { iconName: "ChevronUp" },
                text: "Move Up",
                onClick: () => updateStepNumber(id, stepNumber -1),
            },
            {
                key: "MoveDown",
                iconProps: { iconName: "ChevronDown" },
                text: "Move Down",
                onClick: () => updateStepNumber(id, stepNumber + 1),
            }
        ],
    };

    return (
        <IconButton
            iconProps={moreIcon}
            menuProps={menuProps}
            onRenderMenuIcon={() => { }}
            {...rest}
        />
    );
};

export default StepsTable;
